import React, { useMemo } from "react"
import { getLayoutData, getPageData, getPages } from "@/pages/api/api"
import { dynamicWithLoader, getPathsQueryString } from "@/utils/utils"
import {
	SEE_ALSO_INFO_BLOCK_QUERY,
	SINGLE_PAGE_QUERY,
	WHAT_WE_DO_CATEGORY_QUERY,
	WHAT_WE_DO_SINGLE_ACTION_QUERY,
} from "@/pages/api/fragments"
import { CONTENT_TYPES, PAGES, SINGLE_PAGE_TYPES } from "@/constants"
import ExtendedContent from "@/components/common/ExtendedContent"
import SubPageBanner from "@/components/Banners/SubPage/SubPageBanner"
import useSetBannerData from "@/hooks/useSetBannerData"
import SeeAlso, { getSeeAlsoData, getSeeAlsoInfo } from "@/components/SeeAlso"
import FundraisersSkeleton from "@/components/Fundraisers/Skeleton"
import PreviewPage from "@/components/common/PreviewPage"
import Fundraisers from "@/components/Fundraisers"

// const Fundraisers = dynamicWithLoader(() => import("@/components/Fundraisers"), <FundraisersSkeleton />)
// const PreviewPage = dynamicWithLoader(() => import("@/components/common/PreviewPage"))

export default function Page({ data }) {
	const slug = data?.slug || ""
	const content = data?.content || []
	// console.log(content);
	const isPreviewPage = slug === PAGES.PREVIEW
	const isFundraisersPage = slug === PAGES.FUNDRAISERS
	const bannerData = useSetBannerData(data)
	const seeAlsoInfo = getSeeAlsoInfo(data)
	const formattedSeeAlsoData = useMemo(() => getSeeAlsoData(data), [data])
	return (
		<div>
			{isPreviewPage ? <PreviewPage content={content} /> : <SubPageBanner data={bannerData} />}
			{isFundraisersPage && <Fundraisers />}
			<ExtendedContent content={content} />
			<SeeAlso header={seeAlsoInfo.header} variant={seeAlsoInfo.variant} data={formattedSeeAlsoData} />
		</div>
	)
}

const EXCLUDE_PATHS = [PAGES.HOME, PAGES.POLISH_RED_CROSS, PAGES.ERROR, PAGES.THANK_YOU]

const pathsQuery = `
	query GetSinglePages($locale: SiteLocale) {
		allSinglePages(locale: $locale, filter: {slug: {notIn: ${getPathsQueryString(EXCLUDE_PATHS)} }, pageType: {eq: "${SINGLE_PAGE_TYPES.SINGLE}"}}) {
            singlePageName
            slug 
		}
	}
`

export const singlePagePropsQuery = `
	query GetSinglePagesData($slug: String, $locale: SiteLocale)  {
		allSinglePages(locale: $locale, filter: {slug: {eq: $slug}}) {
			__typename
			${SINGLE_PAGE_QUERY}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			seeAlsoLinks {
				__typename
				... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
					${WHAT_WE_DO_SINGLE_ACTION_QUERY}
				}
				... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
					${WHAT_WE_DO_CATEGORY_QUERY}
				}
				... on ${CONTENT_TYPES.SINGLE_PAGE} {
					__typename
					id
					pageType
					slug
					card {
						header
						description
					}
				}
				... on ${CONTENT_TYPES.ALL_NEWS_PAGE} {
					__typename
					id
					slug
					card {
						header
						description
					}
				}
		    }
        }
    }
`

export async function getStaticProps({ params, locale, preview = false }) {
	const { slug } = params
	// logNamed({ preview })
	const data = await getPageData("allSinglePages", slug, singlePagePropsQuery, locale, "", preview)

	const layout = await getLayoutData(locale, preview)
	return {
		props: {
			data,
			locale,
			...layout,
			isDarkSearch: true,
			preview,
		},
		revalidate: 60 * 2,
	}
}

export async function getStaticPaths({ locales }) {
	const paths = []
	for (const locale of locales) {
		const singlePages = await getPages("allSinglePages", pathsQuery, locale)
		const single = singlePages
			.map((page) => {
				return {
					params: {
						slug: page.slug,
					},
					locale,
				}
			})
			.filter((el) => {
				return el !== null
			})
		paths.push(...single)
	}
	return {
		paths,
		fallback: false,
	}
}